// The smaller pages: Stripe's way back, the sign-in callback, and the page for an address that does not exist.

import type { PageContext } from '../router.ts';
import { loginCallback } from '../auth.ts';
import { config } from '../config.ts';
import { h, svg } from '../dom.ts';
import { t, tf } from '../i18n.ts';
import { log } from '../logs.ts';
import { track } from '../metrics.ts';

const K = 'influencer.web.stripeHandoff';

const checkIcon = () => svg(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="35" height="35" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>`);
const linkOffIcon = () => svg(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="35" height="35" fill="currentColor" aria-hidden="true"><path d="M17 7h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.43-.98 2.63-2.31 2.98l1.46 1.46C20.88 15.61 22 13.95 22 12c0-2.76-2.24-5-5-5zm-1 4h-2.19l2 2H16zM2 4.27l3.11 3.11C3.29 8.12 2 9.91 2 12c0 2.76 2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1 0-1.59 1.21-2.9 2.76-3.07L8.73 11H8v2h2.73L13 15.27V17h1.73l4.01 4L20 19.74 3.27 3 2 4.27z"/></svg>`);

/**
 * /stripe/return and /stripe/refresh: where Stripe hands the account holder back after payout onboarding. On a phone
 * the app usually claims the address first; this page is what happens when it does not, so it is a real
 * destination: it tries the app's scheme once for a caller that came from the app, and always offers a button.
 * @param {import("../router.js").PageContext} ctx
 */
export function stripeReturn(ctx: PageContext) {
    const isRefresh = location.pathname.toLowerCase().startsWith('/stripe/refresh');
    const cameFromApp = !!(ctx.q('source') || '').trim();

    const title = () => (isRefresh
        ? tf(`${K}.refresh.title`, 'This link has expired')
        : tf(`${K}.return.title`, 'You are all set'));
    const subtitle = () => (isRefresh
        ? tf(`${K}.refresh.subtitle`, 'Stripe onboarding links are valid for a few minutes. Start the connection again to get a fresh one.')
        : tf(`${K}.return.subtitle`, 'Stripe has your details. Payouts become available once Stripe finishes reviewing them.'));

    // Only where this page ends the journey: an app counts its own completion.
    if (!isRefresh && !cameFromApp) track('influencerStripeLinkCompleted');

    function openApp() {
        const base = config.deepLinkUrl;

        if (!base) {
            log.warn('UrlSettings.DeepLinkUrl is not configured; cannot hand off to the app');

            return;
        }

        // A plain navigation, so a scheme with no app behind it still leaves this page to go back to.
        location.assign(`${base}${base.includes('?') ? '&' : '?'}stripe=${isRefresh ? 'refresh' : 'return'}`);
    }

    const toDashboard = () => ctx.go('/dashboard');

    ctx.render(() => {
        document.title = title();

        return h('div', { class: 'page-stripe' },
            h('div', { class: 'stripe-return' },
                h('div', { class: `icon ${isRefresh ? 'icon-warning' : 'icon-success'}` }, isRefresh ? linkOffIcon() : checkIcon()),
                h('span', { class: 'title' }, title()),
                h('span', { class: 'subtitle' }, subtitle()),
                cameFromApp
                    ? [
                        h('button', { type: 'button', class: 'btn primary-button', onclick: openApp },
                            tf(`${K}.openAppButton.text`, 'Open the app')),
                        h('button', { type: 'button', class: 'btn secondary-button', onclick: toDashboard },
                            tf(`${K}.continueInBrowserButton.text`, 'Continue in the browser'))
                    ]
                    : h('button', { type: 'button', class: 'btn primary-button', onclick: toDashboard },
                        tf(`${K}.backToDashboardButton.text`, 'Back to the dashboard'))));
    });

    // Once, after the first paint: if the scheme has no handler the browser may show its own error over this page.
    if (cameFromApp) requestAnimationFrame(() => setTimeout(openApp, 0));
}

/**
 * /identity-signin: identity sends the person back here with a code, which becomes the session; then to the page they
 * set out for.
 * @param {import("../router.js").PageContext} ctx
 * @param {{ login: () => void, onLanguage: (name: string) => Promise<void> }} actions
 */
export function signin(ctx: PageContext, actions: { login: () => void; onLanguage: (name: string) => Promise<void>; }) {
    const code = ctx.q('code');
    const state = ctx.q('state');

    // The code is exchanged in a moment: a loader would only flash, so the page stays empty meanwhile.

    if (!code || !code.trim()) {
        actions.login();

        return;
    }

    ctx.run(async () => {
        const signedIn = await loginCallback(code, actions.onLanguage);

        // Back to where the person set out from - this cabinet's own pages only; a refused exchange goes home.
        let target = '/';

        if (signedIn && state && state.trim() && !state.includes('identity-signin')) {
            try {
                const url = new URL(state, location.origin);

                if (url.origin === location.origin) target = url.pathname + url.search + url.hash;
            } catch {
                target = '/';
            }
        }

        ctx.go(target, { replace: true });
    });
}

/** Any address the cabinet does not have. @param {import("../router.js").PageContext} ctx */
export function notFound(ctx: PageContext) {
    ctx.title('influencer.web.404.title', 'Page not found');

    ctx.render(() => h('div', { class: 'page-not-found' },
        h('div', { class: 'content' },
            h('h1', null, '404'),
            h('span', { class: 'title' }, t('influencer.web.404.title')),
            h('span', { class: 'subtitle' }, t('influencer.web.404.subtitle')),
            h('button', { type: 'button', class: 'btn go-home-button', onclick: () => ctx.go('/') },
                t('influencer.web.404.backToHomeButton.text')))));
}
